/**
 * Form step — RN counterpart of the web widget's `components/steps/form-step.tsx`.
 *
 * The validation rules, their messages and their timing (validate on submit,
 * clear a field's error the moment it changes) are ported unchanged, because
 * the agent on the other end reads these values and the operator wrote these
 * messages.
 *
 * Three controls have no RN primitive and are rebuilt from a `<Touchable>`:
 * `<select>` becomes an inline option list, and both checkbox flavours become
 * a drawn box. `date` has no picker either and falls through to a text field —
 * a real picker means a new dependency.
 */

import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { Animated, Easing, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, TextInput, View, type KeyboardTypeOptions, type ViewStyle } from "react-native";
import { Calendar, Check, ChevronDown, FileText, List, LoaderCircle, Mail, Phone, SquareCheckBig, Type, type LucideIcon } from "lucide-react-native";
import type { ComponentTheme } from "@ringg/core";

import type { FormField, FormFieldOption, FormFieldType, FormPayload } from "../../lib/component-types";
import { GradientFill, solidColor } from "../../lib/gradient";
import { getButtonRadius, mergeTheme, toNumber } from "../../lib/theme-utils";
import { resolveFontFamily, useWidgetTheme } from "../../lib/widget-theme";
import { Touchable } from "../ui/pressable";

type FormValue = string | number | boolean | string[];

interface FormStepProps {
  payload: FormPayload;
  isProcessing?: boolean;
  onComplete: (formData: Record<string, FormValue>) => void;
}

interface FieldControlProps {
  field: FormField;
  value: FormValue | undefined;
  theme: Required<ComponentTheme>;
  fontFamily: string | undefined;
  hasError: boolean;
  onChange: (value: FormValue) => void;
  /** Multiselect only — adds or removes one option from the array value. */
  onToggleOption: (optionValue: string, checked: boolean) => void;
}

interface CheckboxProps {
  checked: boolean;
  /** Web sizes the two checkbox flavours differently (`w-3.5` vs `w-4`). */
  size: number;
  theme: Required<ComponentTheme>;
}

interface OptionRowProps {
  label: string;
  selected: boolean;
  /** The placeholder row, which reads as absence rather than a choice. */
  muted?: boolean;
  theme: Required<ComponentTheme>;
  fontFamily: string | undefined;
  onPress: () => void;
}

/** Keyboard and correction behaviour a field type asks for. */
interface TextInputTraits {
  keyboardType: KeyboardTypeOptions;
  autoCapitalize: "none" | "sentences";
  autoCorrect: boolean;
}

/** `animate-spin` — one linear turn per second, the CSS default. */
const SPIN_DURATION_MS = 1000;

/** Used only if a theme ships an unparseable `borderRadius`. */
const FIELD_RADIUS_FALLBACK = 8;

/** Half the gap between stacked rows, so neighbouring touch areas never overlap. */
const OPTION_HIT_SLOP = 3;

/** `SquareCheckBig` is lucide's current name for the web source's `CheckSquare`. */
const fieldIcons: Record<string, LucideIcon> = {
  text: Type,
  email: Mail,
  tel: Phone,
  number: Type,
  select: ChevronDown,
  multiselect: List,
  textarea: FileText,
  boolean: SquareCheckBig,
  date: Calendar,
};

/**
 * Web resolves the component theme from a context that `interactive-flow`
 * installs. RN resolves it at the leaf: the inputs are the same — widget
 * colours as the base, the payload's own `theme` on top — and a step rendered
 * on its own, outside a flow, still themes correctly.
 */
const useStepTheme = (componentTheme?: ComponentTheme): Required<ComponentTheme> => {
  const widgetTheme = useWidgetTheme();

  return useMemo(
    () =>
      mergeTheme({
        primaryColor: widgetTheme.primaryColor,
        primaryTextColor: widgetTheme.primaryTextColor,
        // Cards read as bordered surfaces sitting ON the panel, so they take
        // the widget background rather than the (greyer) surface colour.
        backgroundColor: widgetTheme.backgroundColor,
        surfaceColor: widgetTheme.surfaceColor,
        textColor: widgetTheme.textColor,
        mutedTextColor: widgetTheme.mutedTextColor,
        borderColor: widgetTheme.borderColor,
        errorColor: widgetTheme.errorColor,
        successColor: widgetTheme.successColor,
        buttonStyle: widgetTheme.buttonStyle,
        // Web inherits the family from the flow container; RN has no cascade,
        // so it travels through the theme and is applied per <Text>.
        fontFamily: widgetTheme.fontFamily,
        ...componentTheme,
      }),
    [componentTheme, widgetTheme],
  );
};

const getOptionLabel = (option: string | FormFieldOption): string => (typeof option === "string" ? option : option.label);

const getOptionValue = (option: string | FormFieldOption): string => (typeof option === "string" ? option : option.value);

const placeholderFor = (field: FormField): string => field.placeholder || `Enter ${field.label.toLowerCase()}`;

const textInputTraits = (type: FormFieldType): TextInputTraits => {
  switch (type) {
    case "email":
      return { keyboardType: "email-address", autoCapitalize: "none", autoCorrect: false };
    case "tel":
      return { keyboardType: "phone-pad", autoCapitalize: "none", autoCorrect: false };
    // No picker without a dependency, so a date is typed. The punctuation
    // keyboard at least puts `-` and `/` on the first layer.
    case "date":
      return { keyboardType: "numbers-and-punctuation", autoCapitalize: "none", autoCorrect: false };
    default:
      return { keyboardType: "default", autoCapitalize: "sentences", autoCorrect: true };
  }
};

const inputSurface = (theme: Required<ComponentTheme>, hasError: boolean, isFocused: boolean): ViewStyle => ({
  backgroundColor: theme.backgroundColor,
  borderWidth: 1,
  // Web draws a 2px focus ring, which RN has no equivalent for, so the border
  // itself carries focus. An error outranks it — it is the more urgent signal.
  borderColor: hasError ? theme.errorColor : isFocused ? solidColor(theme.primaryColor) : theme.borderColor,
  // Fields follow the card radius, not the button radius — pill-shaped
  // textareas read badly.
  borderRadius: toNumber(theme.borderRadius, FIELD_RADIUS_FALLBACK),
});

const validateField = (field: FormField, value: FormValue | undefined): string | null => {
  // Handle required validation for different types
  if (field.required) {
    if (value === undefined || value === null || value === "") {
      return `${field.label} is required`;
    }
    if (Array.isArray(value) && value.length === 0) {
      return `${field.label} is required`;
    }
    if (typeof value === "string" && !value.trim()) {
      return `${field.label} is required`;
    }
  }

  // String-specific validations
  if (typeof value === "string" && value) {
    if (field.validation) {
      if (field.validation.minLength && value.length < field.validation.minLength) {
        return field.validation.message || `${field.label} must be at least ${field.validation.minLength} characters`;
      }
      if (field.validation.maxLength && value.length > field.validation.maxLength) {
        return field.validation.message || `${field.label} must be at most ${field.validation.maxLength} characters`;
      }
      if (field.validation.pattern) {
        const regex = new RegExp(field.validation.pattern);
        if (!regex.test(value)) {
          return field.validation.message || `${field.label} is invalid`;
        }
      }
    }

    if (field.type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }

    if (field.type === "tel") {
      const phoneRegex = /^[\d\s\-+()]{7,}$/;
      if (!phoneRegex.test(value)) {
        return "Please enter a valid phone number";
      }
    }
  }

  return null;
};

/** `LoaderCircle` is lucide's current name for the web source's `Loader2`. */
const Spinner: FC<{ color: string }> = ({ color }) => {
  const turn = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(Animated.timing(turn, { toValue: 1, duration: SPIN_DURATION_MS, easing: Easing.linear, useNativeDriver: true }));
    loop.start();
    return () => loop.stop();
  }, [turn]);

  return (
    <Animated.View style={{ transform: [{ rotate: turn.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] }) }] }}>
      <LoaderCircle size={16} color={color} />
    </Animated.View>
  );
};

/** Stands in for `<input type="checkbox">`, whose `accentColor` is the fill. */
const Checkbox: FC<CheckboxProps> = ({ checked, size, theme }) => (
  <GradientFill color={checked ? theme.primaryColor : theme.backgroundColor} style={[styles.checkbox, { width: size, height: size, borderColor: checked ? solidColor(theme.primaryColor) : theme.borderColor }]}>
    {checked ? <Check size={size - 4} color={theme.primaryTextColor} strokeWidth={3} /> : null}
  </GradientFill>
);

const OptionRow: FC<OptionRowProps> = ({ label, selected, muted = false, theme, fontFamily, onPress }) => (
  <Touchable style={styles.optionRow} hitSlop={OPTION_HIT_SLOP} onPress={onPress}>
    <Text style={[styles.optionLabel, { color: muted ? theme.mutedTextColor : theme.textColor, fontFamily }, selected ? styles.optionLabelSelected : null]} numberOfLines={1}>
      {label}
    </Text>
    {selected ? <Check size={14} color={solidColor(theme.primaryColor)} /> : null}
  </Touchable>
);

const FieldControl: FC<FieldControlProps> = ({ field, value, theme, fontFamily, hasError, onChange, onToggleOption }) => {
  const [isFocused, setIsFocused] = useState(false);
  // A DOM number input holds half-typed text ("12.", "-") in the node itself
  // and reports a value only once it parses. RN's TextInput has no such
  // buffer, so the raw text lives here and `formData` keeps the number the web
  // version would have submitted.
  const [numberText, setNumberText] = useState(typeof value === "number" ? String(value) : "");

  const surface = inputSurface(theme, hasError, isFocused);
  const textStyle = [styles.input, surface, { color: theme.textColor, fontFamily }];
  const focusHandlers = { onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false) };

  switch (field.type) {
    case "select": {
      if (!field.options) return null;
      const selected = typeof value === "string" ? value : "";

      // RN has no `<select>`, and a modal picker would hide the choices behind
      // a tap the web version does not ask for. The options are rendered
      // inline instead, keeping the DOM's empty placeholder option as the row
      // that clears the field.
      return (
        <View style={[styles.optionList, surface]}>
          <OptionRow label={field.placeholder || `Select ${field.label.toLowerCase()}`} muted selected={selected === ""} theme={theme} fontFamily={fontFamily} onPress={() => onChange("")} />
          {field.options.map((option, idx) => (
            <OptionRow key={idx} label={getOptionLabel(option)} selected={selected === getOptionValue(option)} theme={theme} fontFamily={fontFamily} onPress={() => onChange(getOptionValue(option))} />
          ))}
        </View>
      );
    }

    case "multiselect": {
      if (!field.options) return null;
      const chosen = Array.isArray(value) ? value : [];

      return (
        <View style={[styles.optionList, surface]}>
          {field.options.map((option, idx) => {
            const optionValue = getOptionValue(option);
            const isChecked = chosen.includes(optionValue);

            return (
              <Touchable key={idx} style={styles.checkRow} hitSlop={OPTION_HIT_SLOP} onPress={() => onToggleOption(optionValue, !isChecked)}>
                <Checkbox checked={isChecked} size={14} theme={theme} />
                <Text style={[styles.optionLabel, { color: theme.textColor, fontFamily }]}>{getOptionLabel(option)}</Text>
              </Touchable>
            );
          })}
        </View>
      );
    }

    case "boolean":
      return (
        <Touchable style={styles.booleanRow} onPress={() => onChange(value !== true)}>
          <Checkbox checked={value === true} size={16} theme={theme} />
          <Text style={[styles.optionLabel, { color: theme.mutedTextColor, fontFamily }]}>{field.placeholder || "Yes"}</Text>
        </Touchable>
      );

    case "textarea":
      return (
        <TextInput
          {...focusHandlers}
          style={[textStyle, styles.textarea]}
          placeholder={placeholderFor(field)}
          placeholderTextColor={theme.mutedTextColor}
          value={typeof value === "string" ? value : ""}
          onChangeText={onChange}
          multiline
          numberOfLines={2}
        />
      );

    case "number":
      return (
        <TextInput
          {...focusHandlers}
          style={textStyle}
          placeholder={placeholderFor(field)}
          placeholderTextColor={theme.mutedTextColor}
          value={numberText}
          onChangeText={(text) => {
            setNumberText(text);
            const parsed = Number(text);
            // A numeric keyboard still allows "1.2.3" and a lone "-", which the
            // DOM would have reported as an empty value.
            onChange(text.trim() === "" || Number.isNaN(parsed) ? "" : parsed);
          }}
          keyboardType="numeric"
        />
      );

    default: {
      const traits = textInputTraits(field.type);

      return (
        <TextInput
          {...focusHandlers}
          style={textStyle}
          placeholder={placeholderFor(field)}
          placeholderTextColor={theme.mutedTextColor}
          value={typeof value === "string" ? value : ""}
          onChangeText={onChange}
          keyboardType={traits.keyboardType}
          autoCapitalize={traits.autoCapitalize}
          autoCorrect={traits.autoCorrect}
        />
      );
    }
  }
};

const FormStep: FC<FormStepProps> = ({ payload, isProcessing, onComplete }) => {
  const theme = useStepTheme(payload.theme);
  const buttonRadius = getButtonRadius(theme.buttonStyle);
  const fontFamily = resolveFontFamily(theme.fontFamily);

  const { data } = payload;
  const [formData, setFormData] = useState<Record<string, FormValue>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (fieldName: string) => {
    setFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const validateAllFields = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    data.fields.forEach((field) => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        errors[field.name] = error;
        isValid = false;
      }
    });

    setFieldErrors(errors);
    return isValid;
  };

  const handleFieldChange = (fieldName: string, value: FormValue) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    clearFieldError(fieldName);
  };

  const handleMultiselectChange = (fieldName: string, optionValue: string, checked: boolean) => {
    setFormData((prev) => {
      const currentValues = (prev[fieldName] as string[] | undefined) || [];
      if (checked) {
        return { ...prev, [fieldName]: [...currentValues, optionValue] };
      } else {
        return { ...prev, [fieldName]: currentValues.filter((v) => v !== optionValue) };
      }
    });
    clearFieldError(fieldName);
  };

  const handleSubmit = () => {
    if (validateAllFields()) {
      onComplete(formData);
    }
  };

  const submitTextColor = isProcessing ? theme.mutedTextColor : theme.primaryTextColor;

  return (
    // The form may be the tallest thing on screen in a host app, so it owns its
    // keyboard handling. With an unbounded parent the ScrollView simply lays
    // out at content height and never steals a scroll gesture from the chat.
    <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined}>
      <ScrollView keyboardShouldPersistTaps="handled" nestedScrollEnabled bounces={false} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <FileText size={16} color={solidColor(theme.primaryColor)} />
          <Text style={[styles.title, { color: theme.textColor, fontFamily }]}>{data.title}</Text>
        </View>

        {data.description ? <Text style={[styles.description, { color: theme.mutedTextColor, fontFamily }]}>{data.description}</Text> : null}

        <View style={styles.fields}>
          {data.fields.map((field) => {
            const FieldIcon = fieldIcons[field.type] ?? Type;
            const error = fieldErrors[field.name];

            return (
              <View key={field.name}>
                <View style={styles.labelRow}>
                  <FieldIcon size={12} color={theme.mutedTextColor} />
                  <Text style={[styles.label, { color: theme.mutedTextColor, fontFamily }]}>{field.label}</Text>
                  {field.required ? <Text style={[styles.label, { color: theme.errorColor, fontFamily }]}>*</Text> : null}
                </View>

                <FieldControl
                  field={field}
                  value={formData[field.name]}
                  theme={theme}
                  fontFamily={fontFamily}
                  hasError={!!error}
                  onChange={(value) => handleFieldChange(field.name, value)}
                  onToggleOption={(optionValue, checked) => handleMultiselectChange(field.name, optionValue, checked)}
                />

                {error ? <Text style={[styles.error, { color: theme.errorColor, fontFamily }]}>{error}</Text> : null}
              </View>
            );
          })}
        </View>

        <Touchable onPress={handleSubmit} disabled={isProcessing}>
          <GradientFill color={isProcessing ? theme.borderColor : theme.primaryColor} style={[styles.submitButton, { borderRadius: buttonRadius }]}>
            {isProcessing ? (
              <>
                <Spinner color={submitTextColor} />
                <Text style={[styles.submitLabel, { color: submitTextColor, fontFamily }]}>Submitting...</Text>
              </>
            ) : (
              <Text style={[styles.submitLabel, { color: submitTextColor, fontFamily }]}>{data.submit_label || "Next"}</Text>
            )}
          </GradientFill>
        </Touchable>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  header: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 8 },
  title: { fontSize: 14, lineHeight: 20, fontWeight: "500" },
  description: { fontSize: 12, lineHeight: 16, marginBottom: 12 },
  fields: { gap: 10, marginBottom: 12 },
  labelRow: { flexDirection: "row", alignItems: "center", gap: 4, marginBottom: 4 },
  label: { fontSize: 12, lineHeight: 16, fontWeight: "500" },
  // No `lineHeight`: Android centres single-line input text off it.
  input: { fontSize: 12, paddingHorizontal: 12, paddingVertical: 8, minHeight: 34 },
  // Web's `rows={2}` at this type size: two 16px lines plus the padding.
  textarea: { minHeight: 50, textAlignVertical: "top" },
  optionList: { padding: 8, gap: 6 },
  optionRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8 },
  optionLabel: { flexShrink: 1, fontSize: 12, lineHeight: 16 },
  optionLabelSelected: { fontWeight: "600" },
  checkRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  checkbox: { borderRadius: 4, borderWidth: 1, alignItems: "center", justifyContent: "center" },
  booleanRow: { flexDirection: "row", alignItems: "center", gap: 8, paddingVertical: 4 },
  error: { fontSize: 12, lineHeight: 16, marginTop: 2 },
  submitButton: { height: 36, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8 },
  submitLabel: { fontSize: 14, lineHeight: 20, fontWeight: "600" },
});

export default FormStep;
